import React, {
  FormEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import { APIProvider } from "@vis.gl/react-google-maps";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Footer } from "../../../components/Footer/Footer";
import {
  GetLabsApiService,
  GetLabsBookingError,
} from "../../../services/GetLabsApiService";
import {
  MobilePhlebotomyRequestConflictError,
  PhlebotomyApiService,
} from "../../../services/PhlebotomyApiService";
import {
  buildPhlebotomyFaqTrackingHandlers,
  buildPhlebotomyFinderTrackingContext,
  trackPhlebotomySearchClick,
} from "../../../services/PhlebotomyTrackingService";
import {
  PHLEBOTOMY_MOBILE_OPTIONS_URL,
  PHLEBOTOMY_SEARCH_URL,
} from "../../../utils/urlConstants";
import PhlebotomyHeader from "../components/PhlebotomyHeader";
import PhlebotomyBackButton from "../components/PhlebotomyBackButton";
import PhlebotomyFAQ from "../components/PhlebotomyFAQ";
import { PageContainer } from "../components/PhlebotomySplitPane.styles";
import {
  AddressTitle,
  AddressSupport,
  FlowPageContent,
  IntakeFormActions,
  IntakeSubmitButton,
} from "./PhlebotomySearchPage.styles";
import {
  CompletionResourceActions,
  CompletionResources,
  ScheduleProgressConnector,
  ScheduleProgressMarker,
  ScheduleProgressStep,
  ScheduleProgressSteps,
  ScheduleProgressTrack,
  StickyBookBar,
} from "./MobilePhlebotomyGetLabsSchedulePage.styles";
import {
  CareTeamScheduleStep,
  ScheduleReviewForm,
} from "./MobilePhlebotomyGetLabsScheduleSections";
import type {
  FieldErrors,
  Fulfillment,
  ScheduleFormData,
  ScheduleStep,
  TimeWindow,
} from "./MobilePhlebotomyGetLabsScheduleSections";
import {
  createAnonymousId,
  formatPhoneForApi,
  getLocalBusinessDateString,
  getLocalDateString,
  isUuid,
  isValidUsPhoneNumber,
  US_STATE_ABBREVIATION_FORMAT_REGEX,
  US_ZIP_CODE_REGEX,
} from "./phlebotomyFormUtils";
import { validateEmail } from "../../../utils/hooks/helpers";
import { MobilePhlebotomyRequestNavigationState } from "./MobilePhlebotomyRequestPage";
import type { PhlebotomyStructuredAddress } from "./PhlebotomyAddressAutocomplete";
import GetLabsAvailabilitySelector, {
  GetLabsAvailabilitySelection,
  GetLabsAvailabilityUnavailableReason,
} from "./GetLabsAvailabilitySelector";

type ScheduleProgressStatus = "complete" | "current" | "upcoming";

const MINIMUM_CARE_TEAM_LEAD_DAYS = 3;
const isPastDate = (value: string): boolean =>
  Boolean(value) && value < getLocalDateString(0);

const emptyForm = (zip?: string, hasKit = false): ScheduleFormData => ({
  firstName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
  dateOfBirth: "",
  estimatedDueDate: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: zip ?? "",
  preferredDate: "",
  preferredTimeWindow: "",
  alternateDate: "",
  alternateTimeWindow: "",
  flexibleScheduling: false,
  hasKit,
  schedulingNotes: "",
  contactConsent: false,
});

const formatSlotTime = (
  iso: string,
  locale?: string,
  timeZone?: string,
): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  });
};

const formatSlotDateTime = (
  iso: string,
  locale?: string,
  timeZone?: string,
): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  });
};

const formatSlotDateTimeWindow = (
  startIso: string,
  endIso: string,
  locale?: string,
  timeZone?: string,
): string =>
  `${formatSlotDateTime(startIso, locale, timeZone)} – ${formatSlotTime(
    endIso,
    locale,
    timeZone,
  )}`;

const getScheduleProgressStatus = (
  index: number,
  stepIndex: number,
): ScheduleProgressStatus => {
  if (index < stepIndex) return "complete";
  if (index === stepIndex) return "current";
  return "upcoming";
};

const formatDateOnly = (dateIso: string, locale?: string): string => {
  const [year, month, day] = dateIso.split("-").map(Number);
  const date = new Date(year, (month || 1) - 1, day || 1);
  return date.toLocaleDateString(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

interface MobilePhlebotomyGetLabsSchedulePageProps {
  /**
   * Reuse the progressive scheduler for the standard care-team path without
   * offering GetLabs availability or booking.
   */
  forceCareTeam?: boolean;
}

const MobilePhlebotomyGetLabsScheduleContent: React.FC<
  MobilePhlebotomyGetLabsSchedulePageProps
> = ({ forceCareTeam: forceCareTeamProp = false }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  // Capture before the idempotency effect below calls history.replace. A
  // genuine options-to-scheduler transition is PUSH; direct/reloaded and
  // browser-restored entries arrive as POP and must use the fallback.
  const [initialNavigationAction] = useState(() => history.action);
  const routerLocation = useLocation<MobilePhlebotomyRequestNavigationState>();
  const query = useMemo(
    () => new URLSearchParams(routerLocation.search),
    [routerLocation.search],
  );
  const zip =
    query.get("zip")?.trim() ||
    routerLocation.state?.initialZip?.trim() ||
    undefined;
  const clinicPublicId = query.get("clinic")?.trim() || undefined;
  const reminderTrackingId = query.get("tracking_id")?.trim();
  const incomingUserId = query.get("user_id")?.trim() || undefined;
  const forceCareTeam = forceCareTeamProp || query.get("fulfillment") === "cs";
  const [analyticsId] = useState(
    () =>
      routerLocation.state?.analyticsId ??
      (reminderTrackingId && isUuid(reminderTrackingId)
        ? reminderTrackingId
        : undefined) ??
      incomingUserId ??
      createAnonymousId(),
  );
  const trackingContext = useMemo(
    () =>
      buildPhlebotomyFinderTrackingContext({
        distinctId: analyticsId,
        clinicPublicId,
        userId: incomingUserId,
        trackingId:
          reminderTrackingId && isUuid(reminderTrackingId)
            ? reminderTrackingId
            : undefined,
        pageName: "MobilePhlebotomySchedulePage",
      }),
    [analyticsId, clinicPublicId, incomingUserId, reminderTrackingId],
  );
  const faqTrackingHandlers = useMemo(
    () => buildPhlebotomyFaqTrackingHandlers(trackingContext),
    [trackingContext],
  );
  const renderCareTeamSupportActions = () => (
    <IntakeFormActions sx={{ flexWrap: "wrap", gap: 2 }}>
      <IntakeSubmitButton
        href="tel:+16504602551"
        variant="unity-primary"
        onClick={faqTrackingHandlers.onSupportPhoneClick}
      >
        {t("phlebotomySearch.mobileSchedule.callCareTeam")}
      </IntakeSubmitButton>
      <IntakeSubmitButton
        href="mailto:support@billiontoone.com"
        variant="unity-primary"
        onClick={faqTrackingHandlers.onSupportEmailClick}
      >
        {t("phlebotomySearch.mobileSchedule.emailCareTeam")}
      </IntakeSubmitButton>
    </IntakeFormActions>
  );

  const [step, setStep] = useState<ScheduleStep>("location");
  const [alreadyScheduled, setAlreadyScheduled] = useState(false);
  const [fulfillment, setFulfillment] = useState<Fulfillment>("care_team");
  const [formData, setFormData] = useState<ScheduleFormData>(() => ({
    ...emptyForm(zip, forceCareTeam),
    firstName: routerLocation.state?.defaultFirstName || "",
    lastName: routerLocation.state?.defaultLastName || "",
    phoneNumber: routerLocation.state?.defaultPhoneNumber || "",
    estimatedDueDate: routerLocation.state?.defaultEstimatedDueDate || "",
  }));
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [getLabsSelection, setGetLabsSelection] =
    useState<GetLabsAvailabilitySelection | null>(null);
  const [addressSearchInput, setAddressSearchInput] = useState("");
  const [selectedAddress, setSelectedAddress] =
    useState<PhlebotomyStructuredAddress | null>(null);
  const [useManualAddress, setUseManualAddress] = useState(false);
  const [resolvingAddress, setResolvingAddress] = useState(false);
  const [fallbackReason, setFallbackReason] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const flowPageContentRef = useRef<HTMLDivElement>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [bookingConflict, setBookingConflict] = useState(false);
  const [submissionId, setSubmissionId] = useState(() =>
    isUuid(routerLocation.state?.requestIdempotencyKey)
      ? (routerLocation.state?.requestIdempotencyKey as string)
      : createAnonymousId(),
  );
  const [success, setSuccess] = useState<
    | {
        kind: "getlabs";
        identifier?: string | null;
        startAt?: string | null;
        endAt?: string | null;
      }
    | { kind: "care_team"; identifier: string; replayed?: boolean }
    | null
  >(null);

  useEffect(() => {
    if (routerLocation.state?.requestIdempotencyKey === submissionId) return;
    history.replace({
      pathname: routerLocation.pathname,
      search: routerLocation.search,
      hash: routerLocation.hash,
      state: {
        ...routerLocation.state,
        requestIdempotencyKey: submissionId,
      },
    });
  }, [
    history,
    routerLocation.hash,
    routerLocation.pathname,
    routerLocation.search,
    routerLocation.state,
    submissionId,
  ]);

  useLayoutEffect(() => {
    if (step === "location") return;
    if (flowPageContentRef.current) flowPageContentRef.current.scrollTop = 0;
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [step]);

  useLayoutEffect(() => {
    if (success) {
      if (flowPageContentRef.current) {
        flowPageContentRef.current.scrollTop = 0;
      }
    }
  }, [success]);

  const earliestCareTeamDate = useMemo(
    () => getLocalBusinessDateString(MINIMUM_CARE_TEAM_LEAD_DAYS),
    [],
  );
  const [addressEditReturnStep, setAddressEditReturnStep] = useState<
    "details" | "times" | null
  >(null);
  const [addressAtEdit, setAddressAtEdit] = useState<string | null>(null);

  const getAddressFingerprint = (address: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    postalCode: string;
  }) =>
    [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.state,
      address.postalCode,
    ]
      .map((value) => value.trim().toLocaleLowerCase())
      .join("|");

  const addressSummary = [
    formData.addressLine1.trim(),
    formData.addressLine2.trim(),
    [formData.city.trim(), formData.state.trim().toUpperCase()]
      .filter(Boolean)
      .join(", "),
    formData.postalCode.trim(),
  ]
    .filter(Boolean)
    .join(" · ");

  const queryWithCurrentZip = () => {
    const next = new URLSearchParams(routerLocation.search);
    if (formData.postalCode.trim()) next.set("zip", formData.postalCode.trim());
    next.delete("fulfillment");
    return next;
  };

  const returnToSearch = () => {
    // Pop only the live options entry that genuinely pushed this scheduler.
    // Direct, restored, and stale-marker entries use replace below so the
    // scheduler cannot remain behind in browser history.
    const returnPath = routerLocation.state?.returnTo?.split("?", 1)[0];
    if (
      initialNavigationAction === "PUSH" &&
      returnPath === PHLEBOTOMY_MOBILE_OPTIONS_URL &&
      routerLocation.state?.returnLocationKey &&
      routerLocation.state.returnLocationKey !== routerLocation.key
    ) {
      history.goBack();
      return;
    }
    if (routerLocation.state?.returnTo) {
      history.replace(
        routerLocation.state.returnTo,
        routerLocation.state.returnSearchState,
      );
      return;
    }
    const next = queryWithCurrentZip();
    history.replace({
      pathname: PHLEBOTOMY_SEARCH_URL,
      search: next.toString() ? `?${next}` : "",
      state: routerLocation.state?.returnSearchState,
    });
  };

  const handleBack = () => {
    setFormError(null);
    if (step === "details") setStep("times");
    else if (step === "times") setStep("location");
    else if (step === "location" && addressEditReturnStep) {
      const returnStep = addressEditReturnStep;
      setAddressEditReturnStep(null);
      const addressChanged = addressAtEdit !== getAddressFingerprint(formData);
      setAddressAtEdit(null);
      if (fulfillment === "getlabs" && !getLabsSelection) {
        // Editing the GetLabs address clears the selected slot and availability.
        // Keep the user on the address step so they can request fresh times.
        setStep("location");
      } else if (!addressChanged) {
        setStep(returnStep);
      } else if (fulfillment === "getlabs") {
        setStep("times");
      } else if (validateLocation()) {
        setStep("times");
      }
    } else if (step === "location") returnToSearch();
  };

  const updatePreferredCareTeamDate = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      preferredDate: value,
      ...(prev.alternateDate === value
        ? { alternateDate: "", alternateTimeWindow: "" }
        : {}),
    }));
    setFieldErrors((prev) => ({
      ...prev,
      preferredDate: undefined,
      alternateDate: undefined,
      alternateTimeWindow: undefined,
    }));
    setFormError(null);
  };

  const updateAlternateCareTeamDate = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      alternateDate: value,
      ...(value ? {} : { alternateTimeWindow: "" }),
    }));
    setFieldErrors((prev) => ({
      ...prev,
      alternateDate: undefined,
      alternateTimeWindow: undefined,
    }));
    setFormError(null);
  };

  const updateAlreadyScheduled = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isAlreadyScheduled = event.target.checked;
    setAlreadyScheduled(isAlreadyScheduled);
    setFormError(null);
    trackPhlebotomySearchClick(
      trackingContext,
      isAlreadyScheduled
        ? "mobile_phlebotomy_already_scheduled_selected"
        : "mobile_phlebotomy_scheduling_needed_selected",
    ).catch(() => undefined);
  };

  const updateKitConfirmation = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const hasKit = event.target.checked;
    setFormData((prev) => ({ ...prev, hasKit }));
    setFormError(null);
    trackPhlebotomySearchClick(
      trackingContext,
      "mobile_phlebotomy_kit_status_selected",
      { kit_status: hasKit ? "has_kit" : "needs_kit" },
    ).catch(() => undefined);
  };

  const updateField =
    (field: keyof ScheduleFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
      setFormError(null);
    };

  const handleAddressSelection = (
    address: PhlebotomyStructuredAddress | null,
  ) => {
    setSelectedAddress(address);
    setFormError(null);
    setFieldErrors((prev) => ({
      ...prev,
      addressLine1: undefined,
      city: undefined,
      state: undefined,
      postalCode: undefined,
    }));
    if (address) {
      setFormData((prev) => ({
        ...prev,
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2,
        city: address.city,
        state: address.state,
        postalCode: address.postalCode,
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
    }));
  };

  const validateLocation = () => {
    const errors: FieldErrors = {};
    const required = t("phlebotomySearch.mobileSchedule.errors.required");
    if (!useManualAddress && !selectedAddress) {
      errors.addressLine1 = t(
        "phlebotomySearch.mobileSchedule.errors.selectAddress",
      );
    } else if (!formData.addressLine1.trim()) {
      errors.addressLine1 = required;
    }
    if (!formData.city.trim()) errors.city = required;
    if (!US_STATE_ABBREVIATION_FORMAT_REGEX.test(formData.state.trim()))
      errors.state = t("phlebotomySearch.mobileSchedule.errors.state");
    if (!US_ZIP_CODE_REGEX.test(formData.postalCode.trim()))
      errors.postalCode = t("phlebotomySearch.mobileSchedule.errors.zip");
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateDetails = () => {
    const errors: FieldErrors = {};
    const required = t("phlebotomySearch.mobileSchedule.errors.required");
    if (!formData.firstName.trim()) errors.firstName = required;
    if (!formData.lastName.trim()) errors.lastName = required;
    if (!isValidUsPhoneNumber(formData.phoneNumber))
      errors.phoneNumber = t("phlebotomySearch.mobileSchedule.errors.phone");
    if (!validateEmail(formData.email.trim()))
      errors.email = t("phlebotomySearch.mobileSchedule.errors.email");
    if (!formData.dateOfBirth) errors.dateOfBirth = required;
    else if (!isPastDate(formData.dateOfBirth))
      errors.dateOfBirth = t(
        "phlebotomySearch.mobileRequest.errors.dateOfBirthInvalid",
      );
    if (!formData.estimatedDueDate) errors.estimatedDueDate = required;
    if (!formData.contactConsent)
      errors.contactConsent = t(
        "phlebotomySearch.mobileSchedule.errors.consent",
      );
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const useCareTeamPreferences = useCallback(
    (message?: string, trackFallback = true) => {
      if (trackFallback) {
        trackPhlebotomySearchClick(
          trackingContext,
          "mobile_phlebotomy_care_team_fallback",
        ).catch(() => undefined);
      }
      setFulfillment("care_team");
      setGetLabsSelection(null);
      setFallbackReason(message || null);
      setFormError(null);
      setAddressEditReturnStep(null);
      setStep("times");
    },
    [trackingContext],
  );

  const handleCareTeamLocationSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (alreadyScheduled) return;
    if (!validateLocation()) return;
    setFormError(null);
    setAddressEditReturnStep(null);
    setFallbackReason(null);
    trackPhlebotomySearchClick(
      trackingContext,
      "mobile_phlebotomy_location_submitted",
    ).catch(() => undefined);

    useCareTeamPreferences(undefined, false);
  };

  const handleGetLabsUnavailable = ({
    address,
    hasKit,
    reason,
  }: {
    address: PhlebotomyStructuredAddress;
    hasKit: boolean;
    reason: GetLabsAvailabilityUnavailableReason;
  }) => {
    handleAddressSelection(address);
    setFormData((prev) => ({ ...prev, hasKit }));
    const reasonCopy = {
      availability_error: t(
        "phlebotomySearch.mobileSchedule.availabilityError",
      ),
      needs_kit: t("phlebotomySearch.mobileSchedule.needsKitCareTeam"),
      no_slots: t("phlebotomySearch.mobileSchedule.noSlots"),
      unserved: t("phlebotomySearch.mobileSchedule.notServiceable"),
    }[reason];
    useCareTeamPreferences(reasonCopy);
  };

  const handleTimesSubmit = (event: FormEvent) => {
    event.preventDefault();
    const errors: FieldErrors = {};
    if (fulfillment === "getlabs" && !getLabsSelection) {
      setFormError(t("phlebotomySearch.mobileSchedule.errors.slot"));
      return;
    }
    if (fulfillment === "care_team") {
      const required = t("phlebotomySearch.mobileRequest.errors.required");
      if (!formData.preferredDate) errors.preferredDate = required;
      else if (formData.preferredDate < earliestCareTeamDate)
        errors.preferredDate = t(
          "phlebotomySearch.mobileRequest.preferredDateHelp",
        );
      if (!formData.preferredTimeWindow) errors.preferredTimeWindow = required;
      if (formData.alternateDate && !formData.alternateTimeWindow)
        errors.alternateTimeWindow = t(
          "phlebotomySearch.mobileRequest.errors.alternateTimeRequired",
        );
      if (formData.alternateTimeWindow && !formData.alternateDate)
        errors.alternateDate = t(
          "phlebotomySearch.mobileRequest.errors.alternateDateRequired",
        );
      if (
        formData.alternateDate &&
        formData.alternateDate < earliestCareTeamDate
      )
        errors.alternateDate = t(
          "phlebotomySearch.mobileRequest.preferredDateHelp",
        );
      else if (
        formData.alternateDate &&
        formData.alternateDate === formData.preferredDate
      )
        errors.alternateDate = t(
          "phlebotomySearch.mobileRequest.errors.alternateDateSameAsPreferred",
        );
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) {
      setFormError(null);
      setStep("details");
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (bookingConflict) return;
    if (submitting) return;
    if (
      fulfillment === "getlabs" &&
      (!getLabsSelection?.slot.key ||
        getAddressFingerprint(getLabsSelection.address) !==
          getAddressFingerprint(formData))
    ) {
      setStep("times");
      setFormError(t("phlebotomySearch.mobileSchedule.errors.slot"));
      return;
    }
    if (!validateDetails()) return;
    setSubmitting(true);
    setFormError(null);
    try {
      if (fulfillment === "getlabs") {
        trackPhlebotomySearchClick(
          trackingContext,
          "mobile_phlebotomy_getlabs_book_submitted",
          { kit_status: formData.hasKit ? "has_kit" : "needs_kit" },
        ).catch(() => undefined);
        const bookingInput = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          phoneNumber: formData.phoneNumber
            .replace(/\D/g, "")
            .replace(/^1(\d{10})$/, "$1"),
          email: formData.email.trim(),
          dateOfBirth: formData.dateOfBirth,
          estimatedDueDate: formData.estimatedDueDate || undefined,
          addressLine1: formData.addressLine1.trim(),
          addressLine2: formData.addressLine2.trim() || undefined,
          city: formData.city.trim(),
          state: formData.state.trim().toUpperCase(),
          postalCode: formData.postalCode.trim(),
          bookingKey: getLabsSelection?.slot.key || "",
          hasKit: formData.hasKit,
          schedulingNotes: formData.schedulingNotes.trim() || undefined,
          contactConsent: formData.contactConsent,
          bookingId: submissionId,
          portalAccountPublicId: clinicPublicId,
          trackingId:
            reminderTrackingId && isUuid(reminderTrackingId)
              ? reminderTrackingId
              : undefined,
        };
        const result = await GetLabsApiService.bookAppointment(bookingInput);
        setSuccess({
          kind: "getlabs",
          identifier: result.identifier,
          startAt: result.startAt,
          endAt: result.endAt,
        });
        trackPhlebotomySearchClick(
          trackingContext,
          "mobile_phlebotomy_getlabs_book_succeeded",
          { kit_status: formData.hasKit ? "has_kit" : "needs_kit" },
        ).catch(() => undefined);
      } else {
        const response =
          await PhlebotomyApiService.submitMobilePhlebotomyRequest({
            request_id: submissionId,
            first_name: formData.firstName.trim(),
            last_name: formData.lastName.trim(),
            phone_number: formatPhoneForApi(formData.phoneNumber),
            email: formData.email.trim(),
            date_of_birth: formData.dateOfBirth,
            estimated_due_date: formData.estimatedDueDate,
            address_line_1: formData.addressLine1.trim(),
            address_line_2: formData.addressLine2.trim(),
            city: formData.city.trim(),
            state: formData.state.trim().toUpperCase(),
            postal_code: formData.postalCode.trim().slice(0, 5),
            preferred_date: formData.preferredDate,
            preferred_time_window: formData.preferredTimeWindow as TimeWindow,
            ...(formData.alternateDate
              ? { alternate_date: formData.alternateDate }
              : {}),
            ...(formData.alternateTimeWindow
              ? {
                  alternate_time_window:
                    formData.alternateTimeWindow as TimeWindow,
                }
              : {}),
            flexible_scheduling: formData.flexibleScheduling,
            has_kit: formData.hasKit,
            scheduling_notes: formData.schedulingNotes.trim(),
            ...(clinicPublicId
              ? { portal_account_public_id: clinicPublicId }
              : {}),
            ...(reminderTrackingId && isUuid(reminderTrackingId)
              ? { tracking_id: reminderTrackingId }
              : {}),
            language: i18n.resolvedLanguage?.startsWith("es") ? "es" : "en",
            contact_consent: formData.contactConsent,
          });
        setSuccess({ kind: "care_team", identifier: response.requestId });
        trackPhlebotomySearchClick(
          trackingContext,
          "mobile_phlebotomy_request_submitted",
          { kit_status: formData.hasKit ? "has_kit" : "needs_kit" },
        ).catch(() => undefined);
      }
    } catch (error) {
      if (
        fulfillment === "care_team" &&
        error instanceof MobilePhlebotomyRequestConflictError
      ) {
        setSuccess({
          kind: "care_team",
          identifier: error.requestId,
          replayed: true,
        });
      } else if (fulfillment === "getlabs") {
        if (
          error instanceof GetLabsBookingError &&
          error.bookingOutcome === "conflict"
        ) {
          setBookingConflict(true);
          setFormError(t("phlebotomySearch.mobileSchedule.bookingConflict"));
          return;
        }
        trackPhlebotomySearchClick(
          trackingContext,
          "mobile_phlebotomy_getlabs_book_failed",
        ).catch(() => undefined);
        let fallbackMessageKey:
          | "phlebotomySearch.mobileSchedule.bookUnknown"
          | "phlebotomySearch.mobileSchedule.bookNotCreated"
          | "phlebotomySearch.mobileSchedule.bookRejected" =
          "phlebotomySearch.mobileSchedule.bookUnknown";
        if (error instanceof GetLabsBookingError) {
          if (error.bookingOutcome === "not_created") {
            fallbackMessageKey =
              "phlebotomySearch.mobileSchedule.bookNotCreated";
          } else if (error.bookingOutcome === "rejected") {
            fallbackMessageKey = "phlebotomySearch.mobileSchedule.bookRejected";
          }
        }
        const fallbackMessage = t(fallbackMessageKey);
        if (
          error instanceof GetLabsBookingError &&
          error.bookingOutcome === "rejected"
        ) {
          setSubmissionId(createAnonymousId());
        }
        useCareTeamPreferences(fallbackMessage);
      } else {
        setFormError(t("phlebotomySearch.mobileRequest.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const stepIndex = { location: 0, times: 1, details: 2 }[step];
  let submitLabel = t("phlebotomySearch.mobileRequest.submit");
  if (fulfillment === "getlabs") {
    submitLabel = t("phlebotomySearch.mobileSchedule.book");
  }
  if (submitting) {
    submitLabel =
      fulfillment === "getlabs"
        ? t("phlebotomySearch.mobileSchedule.booking")
        : t("phlebotomySearch.mobileRequest.submitting");
  }
  let appointmentSummary = "";
  if (fulfillment === "getlabs" && getLabsSelection) {
    appointmentSummary = formatSlotDateTimeWindow(
      getLabsSelection.slot.start,
      getLabsSelection.slot.end,
      i18n.language,
      getLabsSelection.timezone,
    );
  } else if (
    fulfillment === "care_team" &&
    formData.preferredDate &&
    formData.preferredTimeWindow
  ) {
    appointmentSummary = `${formatDateOnly(
      formData.preferredDate,
      i18n.language,
    )} · ${t(
      `phlebotomySearch.mobileRequest.timeWindows.${formData.preferredTimeWindow}`,
    )}`;
    if (formData.alternateDate && formData.alternateTimeWindow) {
      appointmentSummary += t(
        "phlebotomySearch.mobileSchedule.alternatePreferenceSummary",
        {
          date: formatDateOnly(formData.alternateDate, i18n.language),
          time: t(
            `phlebotomySearch.mobileRequest.timeWindows.${formData.alternateTimeWindow}`,
          ),
        },
      );
    }
  }

  let completionTitle = "";
  let completionSupport = "";
  if (success?.kind === "getlabs") {
    completionTitle = t("phlebotomySearch.mobileSchedule.successTitle");
    completionSupport = t("phlebotomySearch.mobileSchedule.successSupport", {
      identifier: success.identifier || "—",
      when:
        success.startAt && success.endAt
          ? formatSlotDateTimeWindow(
              success.startAt,
              success.endAt,
              i18n.language,
              getLabsSelection?.timezone,
            )
          : "—",
    });
  } else if (success?.kind === "care_team") {
    completionTitle = t("phlebotomySearch.mobileSchedule.careTeamSuccessTitle");
    completionSupport = t(
      "phlebotomySearch.mobileSchedule.careTeamSuccessSupport",
    );
  }

  const careTeamStepProps = {
    formData,
    fieldErrors,
    addressSearchInput,
    selectedAddress,
    useManualAddress,
    resolvingAddress,
    alreadyScheduled,
    fallbackReason,
    formError,
    addressSummary,
    earliestCareTeamDate,
    onLocationSubmit: handleCareTeamLocationSubmit,
    onTimesSubmit: handleTimesSubmit,
    onFieldChange: updateField,
    onAddressSearchInputChange: setAddressSearchInput,
    onAddressSelection: handleAddressSelection,
    onAddressBusyChange: setResolvingAddress,
    onToggleManualAddress: () => {
      if (useManualAddress) {
        handleAddressSelection(null);
        setAddressSearchInput("");
      } else {
        setSelectedAddress(null);
        setFieldErrors((prev) => ({ ...prev, addressLine1: undefined }));
      }
      setUseManualAddress(!useManualAddress);
    },
    onKitChange: updateKitConfirmation,
    onAlreadyScheduledChange: updateAlreadyScheduled,
    onEditAddress: () => {
      setAddressAtEdit(getAddressFingerprint(formData));
      setAddressEditReturnStep("times");
      setStep("location");
    },
    onPreferredDateChange: updatePreferredCareTeamDate,
    onAlternateDateChange: updateAlternateCareTeamDate,
    supportActions: renderCareTeamSupportActions(),
  };

  return (
    <PageContainer>
      <PhlebotomyHeader />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          bgcolor: "background.paper",
        }}
      >
        <FlowPageContent
          ref={flowPageContentRef}
          data-testid="getlabs-schedule-content"
        >
          {/* Use the same header-to-back-to-title rhythm as Change location
                and the other mobile scheduling flows. */}
          {!success && !submitting && (
            <PhlebotomyBackButton onClick={handleBack} />
          )}
          {success ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CheckCircleOutlineIcon
                sx={{ fontSize: 48, color: "success.main", mb: 2 }}
              />
              <AddressTitle component="h1">{completionTitle}</AddressTitle>
              <AddressSupport>{completionSupport}</AddressSupport>
              {success.kind === "care_team" && success.replayed && (
                <Alert severity="warning" sx={{ mt: 2, textAlign: "left" }}>
                  {t("phlebotomySearch.mobileRequest.replayedWarning")}
                </Alert>
              )}
              {success.kind === "care_team" && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  {t("phlebotomySearch.mobileRequest.requestReference", {
                    requestId: success.identifier,
                  })}
                </Typography>
              )}
              <CompletionResources>
                <Typography component="h2" variant="h6" fontWeight={700}>
                  {t(
                    "phlebotomySearch.mobileSchedule.completionResourcesTitle",
                  )}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  {t(
                    "phlebotomySearch.mobileSchedule.completionResourcesSupport",
                  )}
                </Typography>
                <CompletionResourceActions>
                  <Button
                    component="a"
                    href="tel:+16504602551"
                    variant="unity-primary"
                    onClick={faqTrackingHandlers.onSupportPhoneClick}
                  >
                    {t("phlebotomySearch.mobileSchedule.callCareTeam")}
                  </Button>
                  <Button
                    component="a"
                    href="mailto:support@billiontoone.com"
                    variant="unity-primary"
                    onClick={faqTrackingHandlers.onSupportEmailClick}
                  >
                    {t("phlebotomySearch.mobileSchedule.emailCareTeam")}
                  </Button>
                </CompletionResourceActions>
              </CompletionResources>
              <PhlebotomyFAQ {...faqTrackingHandlers} />
            </Box>
          ) : (
            <>
              <AddressTitle component="h1">
                {t("phlebotomySearch.mobileSchedule.formTitle")}
              </AddressTitle>
              <Box sx={{ mt: 1.5, mb: 2 }}>
                <ScheduleProgressTrack
                  role="progressbar"
                  aria-label={t("phlebotomySearch.mobileSchedule.stepsLabel")}
                  aria-valuemin={1}
                  aria-valuemax={3}
                  aria-valuenow={stepIndex + 1}
                  aria-valuetext={t(
                    "phlebotomySearch.mobileSchedule.progressValue",
                    {
                      current: stepIndex + 1,
                      total: 3,
                      step: [
                        t("phlebotomySearch.mobileSchedule.stepAddress"),
                        t("phlebotomySearch.mobileSchedule.stepTime"),
                        t("phlebotomySearch.mobileSchedule.stepDetails"),
                      ][stepIndex],
                    },
                  )}
                >
                  <ScheduleProgressConnector aria-hidden="true" />
                  <ScheduleProgressSteps aria-hidden="true">
                    {[
                      t("phlebotomySearch.mobileSchedule.stepAddress"),
                      t("phlebotomySearch.mobileSchedule.stepTime"),
                      t("phlebotomySearch.mobileSchedule.stepDetails"),
                    ].map((title, index) => {
                      const status = getScheduleProgressStatus(
                        index,
                        stepIndex,
                      );
                      return (
                        <ScheduleProgressStep key={title} status={status}>
                          <ScheduleProgressMarker status={status}>
                            {status === "complete" ? (
                              <CheckIcon
                                data-testid="schedule-step-check"
                                aria-hidden="true"
                              />
                            ) : (
                              index + 1
                            )}
                          </ScheduleProgressMarker>
                          {title}
                        </ScheduleProgressStep>
                      );
                    })}
                  </ScheduleProgressSteps>
                </ScheduleProgressTrack>
              </Box>

              {!forceCareTeam && (
                <Box
                  sx={{
                    display:
                      step === "location" ||
                      (step === "times" && fulfillment === "getlabs")
                        ? "contents"
                        : "none",
                  }}
                >
                  <GetLabsAvailabilitySelector
                    step={step === "location" ? "location" : "times"}
                    address={selectedAddress}
                    addressLine2={formData.addressLine2}
                    hasKit={formData.hasKit}
                    alreadyScheduledActions={renderCareTeamSupportActions()}
                    onAddressChange={handleAddressSelection}
                    onAddressLine2Change={(addressLine2) => {
                      setSelectedAddress((address) =>
                        address ? { ...address, addressLine2 } : null,
                      );
                      setFormData((prev) => ({ ...prev, addressLine2 }));
                    }}
                    onAlreadyScheduledChange={(isAlreadyScheduled) => {
                      trackPhlebotomySearchClick(
                        trackingContext,
                        isAlreadyScheduled
                          ? "mobile_phlebotomy_already_scheduled_selected"
                          : "mobile_phlebotomy_scheduling_needed_selected",
                      ).catch(() => undefined);
                    }}
                    onKitStatusChange={(hasKit) => {
                      setFormData((prev) => ({ ...prev, hasKit }));
                      trackPhlebotomySearchClick(
                        trackingContext,
                        "mobile_phlebotomy_kit_status_selected",
                        { kit_status: hasKit ? "has_kit" : "needs_kit" },
                      ).catch(() => undefined);
                    }}
                    onSelectionChange={(selection) => {
                      setGetLabsSelection(selection);
                      if (selection) {
                        handleAddressSelection(selection.address);
                        setFormData((prev) => ({
                          ...prev,
                          hasKit: selection.hasKit,
                        }));
                        setFormError(null);
                        trackPhlebotomySearchClick(
                          trackingContext,
                          "mobile_phlebotomy_slot_selected",
                        ).catch(() => undefined);
                      }
                    }}
                    onStepChange={(nextStep) => {
                      setStep(nextStep);
                      if (nextStep === "times") {
                        setAddressEditReturnStep(null);
                        setFulfillment("getlabs");
                        setFallbackReason(null);
                        trackPhlebotomySearchClick(
                          trackingContext,
                          "mobile_phlebotomy_availability_shown",
                        ).catch(() => undefined);
                      } else if (nextStep === "location") {
                        setAddressAtEdit(getAddressFingerprint(formData));
                        setAddressEditReturnStep("times");
                      }
                    }}
                    onUnavailable={handleGetLabsUnavailable}
                  />
                </Box>
              )}

              {step === "times" && fulfillment === "getlabs" && (
                <StickyBookBar>
                  <IntakeSubmitButton
                    type="button"
                    variant="unity-primary"
                    disabled={!getLabsSelection}
                    onClick={() => {
                      if (getLabsSelection) setStep("details");
                    }}
                  >
                    {t("phlebotomySearch.mobileSchedule.continueToDetails")}
                  </IntakeSubmitButton>
                </StickyBookBar>
              )}

              {step === "location" && forceCareTeam && (
                <CareTeamScheduleStep step="location" {...careTeamStepProps} />
              )}

              {step === "times" && fulfillment === "care_team" && (
                <CareTeamScheduleStep step="times" {...careTeamStepProps} />
              )}

              {step === "details" && (
                <ScheduleReviewForm
                  formData={formData}
                  fieldErrors={fieldErrors}
                  fulfillment={fulfillment}
                  appointmentSummary={appointmentSummary}
                  addressSummary={addressSummary}
                  submitting={submitting}
                  bookingConflict={bookingConflict}
                  formError={formError}
                  submitLabel={submitLabel}
                  onSubmit={handleSubmit}
                  onFieldChange={updateField}
                  onEditTimes={() => setStep("times")}
                  onEditAddress={() => {
                    setAddressAtEdit(getAddressFingerprint(formData));
                    setAddressEditReturnStep("details");
                    setStep("location");
                  }}
                  supportActions={renderCareTeamSupportActions()}
                />
              )}
            </>
          )}
        </FlowPageContent>
      </Box>
      <Footer showLanguageSwitcher requireLanguageFeatureFlag={false} />
    </PageContainer>
  );
};

const MobilePhlebotomyGetLabsSchedulePage: React.FC<
  MobilePhlebotomyGetLabsSchedulePageProps
> = ({ forceCareTeam = false }) => (
  <APIProvider
    apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ""}
    libraries={["places"]}
  >
    <MobilePhlebotomyGetLabsScheduleContent forceCareTeam={forceCareTeam} />
  </APIProvider>
);

export default MobilePhlebotomyGetLabsSchedulePage;
